import { SignedOut, SignInButton } from '@clerk/react-router'
import { Bars3Icon, ChevronDownIcon, UserIcon } from '@heroicons/react/24/outline'
import classNames from 'classnames'
import { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router'

type NavLink = { label: string; link: string }

export function HamburgerMenu({ links, scrolled, categoryLinks = [] }: { links: NavLink[]; scrolled?: boolean; categoryLinks?: NavLink[] }) {
  const [open, setOpen] = useState(false)
  const [categoriesOpen, setCategoriesOpen] = useState(false)
  const btnRef = useRef<HTMLButtonElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const { pathname } = useLocation()

  // Sluit bij route change
  useEffect(() => setOpen(false), [pathname])

  // Sluit bij klik buiten en Escape
  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (!open) return
      const t = e.target as Node
      if (!menuRef.current?.contains(t) && !btnRef.current?.contains(t)) {
        setOpen(false)
      }
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onClickOutside)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  return (
    <div className="relative">
      {/* Trigger */}
      <button
        ref={btnRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Open menu"
        onClick={() => setOpen(o => !o)}
        className="inline-flex items-center justify-center rounded-md p-2 transition hover:bg-(--ws-header-hover) focus:ring-2 focus:ring-offset-2 focus:outline-none">
        <Bars3Icon className={classNames('transition-all duration-300', scrolled ? 'h-5 w-5' : 'h-6 w-6')} />
      </button>

      {/* Menu */}
      {open && (
        <div
          ref={menuRef}
          role="menu"
          aria-orientation="vertical"
          className="ease-soft shadow-float absolute left-0 z-50 mt-2 w-56 origin-top-left overflow-hidden rounded-(--ws-radius) border border-(--ws-border) bg-(--ws-header-bg) text-(--ws-header-text) motion-safe:animate-[ws-pop-in_150ms_var(--ease-soft)]">
          <ul className="py-2 text-sm">
            {links.map((item, index) => (
              <li key={item.link}>
                <Link
                  to={item.link}
                  onClick={() => setOpen(false)}
                  role="menuitem"
                  className="block px-4 py-2 transition-colors hover:bg-(--ws-header-hover) focus:outline-none">
                  {item.label}
                </Link>
                {/* Insert categories foldout after Home (index 0) */}
                {index === 0 && categoryLinks.length > 0 && (
                  <div>
                    <button
                      type="button"
                      onClick={() => setCategoriesOpen(o => !o)}
                      className="flex w-full items-center justify-between px-4 py-2 transition-colors hover:bg-(--ws-header-hover) focus:outline-none">
                      <span>Categorieën</span>
                      <ChevronDownIcon
                        className={classNames('h-4 w-4 transition-transform duration-200', {
                          'rotate-180': categoriesOpen
                        })}
                      />
                    </button>
                    {categoriesOpen && (
                      <ul className="bg-(--ws-header-tint)">
                        {categoryLinks.map(cat => (
                          <li key={cat.link}>
                            <Link
                              to={cat.link}
                              onClick={() => setOpen(false)}
                              role="menuitem"
                              className="block py-2 pr-4 pl-8 transition-colors hover:bg-(--ws-header-hover) focus:outline-none">
                              {cat.label}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </li>
            ))}

            {/* Sign in button - only shown when signed out */}
            <SignedOut>
              <li>
                <div className="border-t border-(--ws-border) px-4 py-3">
                  <SignInButton mode="modal">
                    <button className="flex w-full items-center gap-2 rounded-(--ws-radius-inner) bg-(--ws-btn2-bg) px-3 py-2 text-sm font-medium text-(--ws-btn2-text) transition-all hover:bg-(--ws-btn2-bg-hover) hover:shadow-md">
                      <UserIcon className="h-5 w-5" />
                      Inloggen
                    </button>
                  </SignInButton>
                </div>
              </li>
            </SignedOut>
          </ul>
        </div>
      )}
    </div>
  )
}
